import type { ResultOf } from "gql.tada";
import { useMemo } from "react";
import { useQuery } from "urql";
import { GetFeedMetricsQuery } from "@/data-access/news-feeds/queries/news-feeds-queries";
import { abbreviateNumber, getPrettyName } from "@/lib/utils/prettyName";
import { useAnalysisParamsForQueries } from "@/store/news-feed/params/useAnalysisParamsForQueries";

export type FeedMetricsData = ResultOf<
  typeof GetFeedMetricsQuery
>["newsFeedStats"];

export interface FeedMetricsStat {
  label: string;
  /** Formatted display value (e.g. "4k"). */
  value: string;
  /** Raw value for clipboard copy. */
  rawValue: string | number;
  color: string;
}

const getSentimentColor = (sentiment: string | null | undefined): string => {
  if (sentiment === "POSITIVE") return "text-green-700";
  if (sentiment === "NEGATIVE") return "text-rose-700/90";
  return "text-gray-600";
};

export const mapFeedMetricsToStats = (
  feedMetrics: FeedMetricsData | undefined | null,
): FeedMetricsStat[] => {
  if (!feedMetrics) {
    return [];
  }

  const totalMentions = feedMetrics.totalMentions
    ? Number(feedMetrics.totalMentions)
    : 0;
  const readership = feedMetrics.readership
    ? Number(feedMetrics.readership)
    : 0;
  const socialEngagement = feedMetrics.socialEngagement
    ? Number(feedMetrics.socialEngagement)
    : 0;
  const netSentiment = feedMetrics.netSentiment ?? "Neutral";

  return [
    {
      label: "Total Mentions",
      value: abbreviateNumber(totalMentions),
      rawValue: totalMentions,
      color: "text-gray-700",
    },
    {
      label: "Net LLM Impact Score",
      value: abbreviateNumber(feedMetrics.netImpactScore ?? 0),
      rawValue: feedMetrics.netImpactScore ?? 0,
      color: "text-violet-800",
    },
    {
      label: "Average Article Impact",
      value: abbreviateNumber(feedMetrics.averageArticleImpact ?? 0),
      rawValue: feedMetrics.averageArticleImpact ?? 0,
      color: "text-violet-800",
    },
    {
      label: "Average Impact Strength",
      value: abbreviateNumber(feedMetrics.averageImpactStrength ?? 0),
      rawValue: feedMetrics.averageImpactStrength ?? 0,
      color: "text-violet-800",
    },
    {
      label: "Readership",
      value: abbreviateNumber(readership),
      rawValue: readership,
      color: "text-gray-700",
    },
    {
      label: "Social Engagements",
      value: abbreviateNumber(socialEngagement),
      rawValue: socialEngagement,
      color: "text-gray-700",
    },
    {
      label: "Net Sentiment",
      value: getPrettyName(netSentiment),
      rawValue: getPrettyName(netSentiment),
      color: getSentimentColor(feedMetrics.netSentiment),
    },
    {
      label: "Gross Impact",
      value: abbreviateNumber(feedMetrics.grossImpact ?? 0),
      rawValue: feedMetrics.grossImpact ?? 0,
      color: "text-gray-700",
    },
    {
      label: "Gross Positive Impact",
      value: abbreviateNumber(feedMetrics.grossPositiveImpact ?? 0),
      rawValue: feedMetrics.grossPositiveImpact ?? 0,
      color: "text-green-600",
    },
    {
      label: "Gross Neutral Impact",
      value: abbreviateNumber(feedMetrics.grossNeutralImpact ?? 0),
      rawValue: feedMetrics.grossNeutralImpact ?? 0,
      color: "text-gray-600",
    },
    {
      label: "Gross Negative Impact",
      value: abbreviateNumber(feedMetrics.grossNegativeImpact ?? 0),
      rawValue: feedMetrics.grossNegativeImpact ?? 0,
      color: "text-red-600",
    },
    {
      label: "Tier 1",
      value: abbreviateNumber(feedMetrics.tier1 ?? 0),
      rawValue: feedMetrics.tier1 ?? 0,
      color: "text-gray-700",
    },
    {
      label: "Tier 2",
      value: abbreviateNumber(feedMetrics.tier2 ?? 0),
      rawValue: feedMetrics.tier2 ?? 0,
      color: "text-gray-700",
    },
    {
      label: "Tier 3",
      value: abbreviateNumber(feedMetrics.tier3 ?? 0),
      rawValue: feedMetrics.tier3 ?? 0,
      color: "text-gray-700",
    },
    {
      label: "Headlines",
      value: abbreviateNumber(feedMetrics.headlines ?? 0),
      rawValue: feedMetrics.headlines ?? 0,
      color: "text-gray-700",
    },
    {
      label: "Features",
      value: abbreviateNumber(feedMetrics.features ?? 0),
      rawValue: feedMetrics.features ?? 0,
      color: "text-gray-700",
    },
    {
      label: "Passing Mentions",
      value: abbreviateNumber(feedMetrics.passingMentions ?? 0),
      rawValue: feedMetrics.passingMentions ?? 0,
      color: "text-gray-700",
    },
    {
      label: "Positive Mentions",
      value: abbreviateNumber(feedMetrics.positiveMentions ?? 0),
      rawValue: feedMetrics.positiveMentions ?? 0,
      color: "text-green-600",
    },
    {
      label: "Neutral Mentions",
      value: abbreviateNumber(feedMetrics.neutralMentions ?? 0),
      rawValue: feedMetrics.neutralMentions ?? 0,
      color: "text-gray-600",
    },
    {
      label: "Negative Mentions",
      value: abbreviateNumber(feedMetrics.negativeMentions ?? 0),
      rawValue: feedMetrics.negativeMentions ?? 0,
      color: "text-red-600",
    },
  ];
};

/**
 * Hook to fetch feed metrics data
 *
 * Fetches metrics data for the current news feed including impact scores,
 * sentiment data, and engagement metrics. Uses the current filters from
 * the news feed context.
 *
 * @returns Object containing:
 * - data: Feed metrics data or undefined
 * - fetching: Boolean indicating if the query is in progress
 * - error: Any error that occurred during the query
 */
export const useGetFeedMetrics = () => {
  const { feedId, selectedFilters, startDate, endDate, chartSelection } =
    useAnalysisParamsForQueries();

  const enabled =
    feedId !== undefined && startDate !== undefined && endDate !== undefined;

  const [result] = useQuery({
    query: GetFeedMetricsQuery,
    variables: {
      filters: {
        feedId,
        ...selectedFilters,
        dateRange: {
          startDate,
          endDate,
        },
        state: "ADDED",
        ...(chartSelection && { chartSelection }),
      },
    },
    pause: !enabled,
  });

  const { data, fetching, error } = result;

  return {
    data: data?.newsFeedStats,
    fetching,
    error,
  };
};

/**
 * Hook to map feed metrics data to stats for display
 *
 * Uses the useGetFeedMetrics hook to fetch data and maps it to the
 * display format with formatted values and colors.
 *
 * @returns Object containing:
 * - stats: Mapped and formatted stats for display
 * - fetching: Boolean indicating if the query is in progress
 * - error: Any error that occurred during the query
 */
export const useMapMetricsToStats = () => {
  const { data, fetching, error } = useGetFeedMetrics();

  const stats = useMemo(() => mapFeedMetricsToStats(data), [data]);

  return {
    stats,
    fetching,
    error,
  };
};
